import {
  ApplicantProfile,
  ApplicantProfileConfig,
  ApplicantProfileConfiguration,
  FieldType,
  Nullable,
  ProfileKey,
  Program,
  User,
} from '@bybeam/platform-types';
import AnswerReview from '@platform-ui-common/components/AnswerReview/index.js';
import { FieldAnswer } from '@platform-ui-common/components/AnswerReview/types.js';
import FieldDetail from '@platform-ui-common/components/FieldDetail/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import Color from '@platform-ui-common/utilities/Color.js';
import { getField } from '@platform-ui/utilities/application/config.js';
import { getApplicationConfiguration } from '@platform-ui/utilities/application/configs/index.js';
import { getFieldAnswersValue } from '@platform-ui/utilities/application/fields/index.js';
import { hasApplicantType } from '@platform-ui/utilities/programs.js';
import cx from 'classnames';

export enum PartnerConfigProfileKey {
  studentId = 'studentId',
  dateOfBirth = 'dateOfBirth',
}
const knownProfileKeys = [PartnerConfigProfileKey.studentId, PartnerConfigProfileKey.dateOfBirth];

const STRETCH_FIELDS = [FieldType.Complex];

const mapKnownFields = (user: User, config: ApplicantProfileConfig, programs: Program[]) =>
  user.applicantProfile &&
  config &&
  knownProfileKeys
    .filter((profileKey) => !!getPartnerConfig(config, profileKey))
    .map((profileKey) => (
      <FieldDetail key={profileKey as string} label={getPartnerConfigLabel(config, profileKey)}>
        {getFormattedProfileAnswer(user, config, programs, profileKey) ?? '-'}
      </FieldDetail>
    ));

const mapAdditionalFields = (user: User, config: ApplicantProfileConfig, programs: Program[]) => {
  const mapDetail = (field: ProfileKey) => (
    <div
      key={field.key}
      className={cx(
        'px-4',
        { 'w-full': STRETCH_FIELDS.includes(field.type) },
        { 'flex-1 w-min min-w-[150px]': !STRETCH_FIELDS.includes(field.type) },
      )}
    >
      <Typography variant="label" tag="dt" color={Color.TextSecondary}>
        {field?.label}
      </Typography>
      <dt>{getFormattedProfileAnswer(user, config, programs, field.key) ?? '-'}</dt>
    </div>
  );

  return config?.profileKeys
    ? [...config.profileKeys]
        .sort((a, b) => {
          if (STRETCH_FIELDS.includes(a.type)) return -1;
          if (STRETCH_FIELDS.includes(b.type)) return 1;
          return 0;
        })
        .filter(
          (profileKey) => !knownProfileKeys.includes(profileKey.key as PartnerConfigProfileKey),
        )
        .map(mapDetail)
    : null;
};

const getPartnerConfig = (
  config: ApplicantProfileConfig,
  searchKey: string,
): Nullable<ProfileKey> => config?.profileKeys?.find((record) => record.key === searchKey);

const getPartnerConfigLabel = (
  config: ApplicantProfileConfig,
  searchKey: string,
): Nullable<string> => getPartnerConfig(config, searchKey)?.label;

const getFormattedProfileAnswer = (
  user: User,
  partnerConfig: ApplicantProfileConfig,
  programs: Program[],
  searchKey: string,
) => {
  const { applicantProfile: profile } = user;
  const config = getPartnerConfig(partnerConfig, searchKey);
  const field = programs
    .filter((program) => hasApplicantType(program, user))
    .map((program) => getApplicationConfiguration(program, user))
    .map((applicationConfig) => getField(applicationConfig, searchKey))
    .find(Boolean);

  let answer: FieldAnswer;
  if (field?.type === FieldType.Address)
    answer = getFieldAnswersValue<string>(`${field.key}Id`, profile.answers);
  else answer = getFieldAnswersValue<FieldAnswer>(searchKey, profile.answers);

  if (!answer || !field || !field?.type || !config) return null;

  return <AnswerReview source={profile} field={field} answer={answer} defaultValue="-" />;
};

const getApplicantProfileConfig = (
  applicantProfileConfigs: ApplicantProfileConfiguration[],
  applicantProfile: ApplicantProfile,
): Nullable<ApplicantProfileConfig> =>
  applicantProfileConfigs?.find(
    (profileConfig) => profileConfig.applicantTypeId === applicantProfile?.applicantType?.id,
  )?.config;

export {
  getPartnerConfig,
  getPartnerConfigLabel,
  getFormattedProfileAnswer,
  getApplicantProfileConfig,
  mapAdditionalFields,
  mapKnownFields,
};
